/** biome-ignore-all lint/nursery/useUniqueElementIds: <explanation> */
import {
  DragDropContext,
  Draggable,
  type DraggableProvided,
  type DraggableStateSnapshot,
  Droppable,
  type DroppableProvided,
  type DropResult,
} from "@hello-pangea/dnd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGlobalState } from "senf-shared/index";
import { tv, type VariantProps } from "tailwind-variants";
import { Icon } from "../../../..";
import { useIsMobile } from "../../../../hooks/customDeviceDetect";
import { cn } from "../../../../util/classNameConstructor";
import { getItemStyle } from "../../../atoms/sortableList/Sortable";
import Select from "../../../core/select/Select";
import Seperator from "../../../core/seperator/Seperator";
import MagicButton from "../../magicButton/MagicButton";
import { AddButton } from "../components/AddRowButton";
import { useRandomSorting } from "../hooks";
import type {
  InputRow,
  Page,
  PageType,
  QuestionBaseProps,
} from "../survey.config";
import { RankingRow } from "./RankingRow";

const SimpleRow = ({
  value,
  columns,
  index,
  handleRankChange,
  provided,
  snapshot,
  limitRanking,
}: {
  value: string[];
  columns: InputRow<"ranking">[];
  index: number;
  handleRankChange: (rowId: string, newRank: number | null) => void;
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  limitRanking: number;
}) => {
  console.log("limitRanking", limitRanking);
  const row = columns[index];
  const [translation] = useGlobalState().translationsState;
  const rankOptions = useMemo(() => {
    return [
      ...Array.from({ length: limitRanking }, (_, i) => ({
        value: (i + 1).toString(),
        text: (i + 1).toString(),
        className: "h-8",
      })),
    ];
  }, [limitRanking]);

  const onRankChange = (value: string) => {
    if (value === "–") {
      handleRankChange(row.uid, null);
    } else {
      handleRankChange(row.uid, Number.parseInt(value, 10));
    }
  };

  const text = useMemo(() => {
    const index = value?.findIndex((item) => item === row.uid);
    return index > -1 ? (index + 1).toString() : "–";
  }, [value, row.uid]);

  return (
    <div
      key={row.uid}
      className={cn(
        "group/item peer/item flex items-center gap-3 w-initial focus-visible:ring-focused-black focus-visible:ring-2 rounded-lg p-2 focus-visible:outline-none focus-visible:bg-surface-highlight-subdued no-touch:hover:bg-surface-highlight-subdued cursor-grab relative focus-visible:z-10",
        snapshot.isDragging &&
          "shadow-xl bg-surface-highlight-subdued-hovered ring-1 ring-focused-default focus-visible:bg-surface-highlight-subdued-hovered",
      )}
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      tabIndex={-1}
      style={{
        ...provided.draggableProps.style,
        transform: provided.draggableProps.style?.transform
          ? provided.draggableProps.style.transform.replace(
              /translate\((.*?),/,
              "translate(0,",
            )
          : undefined,
      }}
    >
      <Select
        data={rankOptions}
        text={text}
        key={text}
        onValueChange={onRankChange}
        useTriggerWidth
        size="md"
        tabIndex={0}
        className={cn("", columns.length >= 10 ? "w-20" : "w-16")}
        aria-hidden
        classNames={{
          trigger: cn(
            "no-touch:group-hover/item:border-border-highlight-subdued",
            snapshot.isDragging && "border-border-highlight-subdued",
          ),
        }}
      />
      <div className="flex-1 w-full">
        {translation?.[row.label || ""] || row.label || ""}
      </div>

      <div
        className="text-text-subdued end-0 w-8 h-full flex items-center justify-center no-touch:opacity-0 no-touch:group-hover/item:opacity-100 group-active/item:opacity-100 cursor-grab"
        {...provided.dragHandleProps}
        tabIndex={-1}
      >
        <Icon
          name="handle-vertical"
          className={cn(
            "no-touch:text-border-highlight-default",
            snapshot.isDragging && "text-border-highlight-default",
          )}
        />
      </div>
    </div>
  );
};

const RankingStyles = tv({
  slots: {
    Outer: "flex w-full flex-col",
    Wrapper: "w-full pb-6",
    Table: "w-full",
  },
});

type RankingProps = Page<"ranking"> &
  VariantProps<typeof RankingStyles> &
  QuestionBaseProps<"ranking"> & {
    isMobile?: boolean;
  };

const Ranking = ({ onChange, ...props }: RankingProps) => {
  const { Outer, Wrapper, Table } = RankingStyles();
  const { t } = useTranslation();
  const isMobileFallback = useIsMobile();
  const isMobile = props.isMobile ?? isMobileFallback;

  const [generatingOptions, setGeneratingOptions] = useState(false);
  const columns = useRandomSorting(props, "columns", props.mode);

  const [rankedOptions, setRankedOptions] = useState<InputRow<"ranking">[]>(
    columns || [],
  );
  const [isDragging, setIsDragging] = useState(false);

  const limitRanking =
    (props.page?.settings.limit?.enabled && props.page?.settings.limit?.max) ||
    rankedOptions.length;

  const handleRankChange = useCallback(
    (rowId: string, newRank: number | null) => {
      setRankedOptions((prevOptions) => {
        let updatedRows = [...prevOptions];
        const currentIndex = updatedRows.findIndex((row) => row.uid === rowId);
        const [movedRow] = updatedRows.splice(currentIndex, 1);

        if (newRank === null) {
          // If newRank is null, move the option to the end
          updatedRows.push({ ...movedRow, rank: null });
        } else {
          // Insert the option at its new position
          updatedRows.splice(newRank - 1, 0, {
            ...movedRow,
            rank: newRank,
          });
        }

        // Recalculate ranks for all options
        updatedRows = updatedRows.map((row, index) => ({
          ...row,
          rank: index < limitRanking ? index + 1 : null,
        }));

        // Call onChange with the updated data, limiting the value array to maxChoices
        if (props.mode === "display") {
          onChange?.({
            value: updatedRows
              .filter((row) => row.rank !== null)
              .map((row) => row.uid)
              .slice(0, limitRanking),
            comment: props.comment || null,
          });
        }
        props.updateQuestion(
          (e) => ({
            ...(e || {}),
            columns: updatedRows,
          }),
          "options",
        );

        return updatedRows;
      });
    },
    [
      props.mode,
      props.updateQuestion,
      props.updateText,
      props.comment,
      limitRanking,
      onChange,
    ],
  );

  const handleUpdateText = (
    value: string | undefined,
    uid: string,
    valueKey: {
      [K in PageType]: keyof InputRow<K>;
    }[PageType] = "label",
  ) => {
    console.log("handleUpdateText", value, uid, valueKey);
    setRankedOptions((prevOptions) =>
      prevOptions.map((row) =>
        row.uid === uid ? { ...row, [valueKey]: value } : row,
      ),
    );

    // If there's a parent updateText function, call it as well
    props.updateItemText?.(value, uid, "columns", valueKey);
  };

  const onDragStart = () => {
    setIsDragging(true);
  };

  const onDragEnd = (result: DropResult) => {
    setIsDragging(false);
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    const draggedOptionId = rankedOptions[sourceIndex].uid;

    handleRankChange(draggedOptionId, destinationIndex + 1);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: we only need to watch array length
  useEffect(() => {
    setRankedOptions(
      columns.map((e) => {
        return { ...e, rank: null };
      }) || [],
    );
    onChange?.({
      value: [],
      comment: props.comment || null,
    });
  }, [columns?.length]);

  return (
    <div className={Outer()}>
      <div className={Wrapper()}>
        {props.mode === "display" ? (
          <div className="flex flex-col -mx-2 p-0 group/container group-container">
            <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
              <Droppable droppableId="columns" direction="vertical">
                {(droppableProvided: DroppableProvided) => (
                  <div
                    ref={droppableProvided.innerRef}
                    className={cn(
                      "duration-300",
                      !isMobile && "rounded-lg",
                      isDragging &&
                        "bg-surface-highlight-subdued ring-8 ring-surface-highlight-subdued",
                    )}
                  >
                    {rankedOptions.map((row, index, columns) => (
                      <Draggable
                        key={row.uid}
                        draggableId={row.uid}
                        index={index}
                      >
                        {(
                          provided: DraggableProvided,
                          snapshot: DraggableStateSnapshot,
                        ) => (
                          <SimpleRow
                            value={props.value}
                            columns={columns}
                            index={index}
                            handleRankChange={handleRankChange}
                            provided={provided}
                            snapshot={snapshot}
                            limitRanking={limitRanking}
                          />
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            {/* <div className="text-sm my-5 mx-2 text-text-subdued [.group-container:has(:focus-visible)_&]:opacity-100 opacity-0 transition-opacity duration-300">
              <div className="flex items-center gap-1" aria-hidden>
                <Icon
                  name="key-space"
                  className="inline"
                  aria-label="spacebar"
                />{" "}
                +{" "}
                <Icon
                  name="key-arrow-up"
                  className="inline"
                  aria-label="arrow keys"
                />{" "}
                /{" "}
                <Icon
                  name="key-arrow-down"
                  className="inline"
                  aria-label="arrow keys"
                />
              </div>
              <span id={"question-description"}>
                {t("survey:accessibility.ranking.description")}
              </span>
            </div> */}
          </div>
        ) : (
          <div className={Table()}>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="columns">
                {(droppableProvided: DroppableProvided) => (
                  <div ref={droppableProvided.innerRef}>
                    {rankedOptions.map((row, index) => (
                      <React.Fragment key={row.uid}>
                        <Draggable draggableId={row.uid} index={index}>
                          {(
                            provided: DraggableProvided,
                            snapshot: DraggableStateSnapshot,
                          ) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              tabIndex={-1}
                              style={getItemStyle(provided, snapshot, "y")}
                              className={cn(isMobile ? "ml-5" : "")}
                            >
                              <RankingRow
                                {...props}
                                type={props.type}
                                index={index}
                                row={row}
                                value={props.value}
                                totalOptions={rankedOptions.length}
                                onRankChange={(newRank) =>
                                  handleRankChange(row.uid, newRank)
                                }
                                mode={props.mode}
                                update={handleUpdateText}
                                currentPage={props.page}
                                rank={row.rank}
                                limitRanking={limitRanking}
                              />
                            </div>
                          )}
                        </Draggable>
                        {props?.value?.length &&
                        props.page?.settings.limit?.enabled &&
                        props.page?.settings.limit?.max &&
                        index === props.page?.settings.limit?.max - 1 ? (
                          <Seperator className="my-2" />
                        ) : null}
                      </React.Fragment>
                    ))}

                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        )}
      </div>
      {props.mode === "edit" && (
        <div className="flex gap-5">
          <AddButton
            text={t("global:option")}
            onClick={() => props.addItem("columns", { focus: true })}
            className="-mx-0.5"
            classNameWrapper="mr-auto"
            id="pageOptions-addButton"
            disabled={generatingOptions}
          />
          <MagicButton
            text={t("util:generate", { val: t("global:options") })}
            disabled={generatingOptions}
            leadingIcon={generatingOptions ? "loading" : "ai"}
            onClick={() => {
              setGeneratingOptions(true);
              props
                .generateItems("columns", props.page, {
                  focus: true,
                  generateType: "more",
                })
                .finally(() => {
                  setGeneratingOptions(false);
                });
            }}
            className="-mx-0.5"
            classNameWrapper="mr-auto"
            id="pageOptions-magicButton"
          />
        </div>
      )}
    </div>
  );
};
Ranking.displayName = "Ranking";
export default Ranking;
